import React, { useState } from 'react';
import { useSnapshot } from 'valtio';
import { editorState } from './editor-state';
import { processLLMResponseAndUpdateState, getMessages, extractTag } from './process-llm';
import ChatUI from './ChatUI';

const Chat = () => {
  const snap = useSnapshot(editorState); // Get the whole snapshot
  const selectedModelFromToolbar = snap.modelName; // Get modelName from editorState
  const messages = snap.chat?.messages || []; // Get messages from editorState
  const specification = snap.component.specification; // Get specification from editorState

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState({}); // Added state for expanded messages

  const toggleExpand = (index) => { // Added function to toggle expand state
    setExpandedMessages(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const detectColors = (text) => {
    const colors = [];
    if (text.toLowerCase().includes('red')) colors.push('red');
    if (text.toLowerCase().includes('yellow')) colors.push('yellow');
    if (text.toLowerCase().includes('green')) colors.push('green');
    return colors;
  };

  const getCost = async (id) => {
    if (!id) {
      console.log('No ID provided to getCost');
      return null; // Return null as per existing behavior
    }
    const apiKey = localStorage.getItem('api-configuration-key') || '';
    const costApiUrl = `https://openrouter.ai/api/v1/generation?id=${id}`;
    let attempts = 0;

    while (attempts < 3) {
      try {
        console.log(`Fetching cost for ID: ${id}, Attempt: ${attempts + 1}`);
        const response = await fetch(costApiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${apiKey}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.text();
          // Increment attempts and continue to retry if not the last attempt
          attempts++;
          if (attempts < 3) {
            await new Promise(resolve => setTimeout(resolve, 500));
            continue;
          }
          console.error(`Failed to fetch cost after 3 attempts: ${response.status} ${response.statusText} - ${errorData}`);
          return null; // Return null after last attempt fails
        }

        const costData = await response.json();
        console.log('Cost data received:', costData);
        return costData; // Return the fetched cost data

      } catch (error) {
        console.error('Error fetching cost:', error);
        attempts++;
        if (attempts < 3) {
          await new Promise(resolve => setTimeout(resolve, 500));
          continue;
        }
        return null; // Return null after last attempt fails due to exception
      }
    }
    return null; // Should be unreachable if logic is correct, but as a fallback
  };

  const handleSubmit = async (e, images = []) => {
    e.preventDefault();
    if ((!input.trim() && images.length === 0) || isLoading) return;

    const userMessage = input;
    setInput('');

    // Convert images to base64 data URLs for storage and display
    const imageDataUrls = images.map(img => img.src);

    // Update to use editorState
    editorState.addMessage({
      role: 'user',
      content: userMessage,
      images: imageDataUrls
    });
    setIsLoading(true);
    let thinkingMessageShown = false;

    // Get API configuration from localStorage
    const apiKey = localStorage.getItem('api-configuration-key') || '';
    const localStorageDefaultModel = localStorage.getItem('api-configuration-model');
    const apiUrl = localStorage.getItem('api-configuration-url');

    // Use model from toolbar selection, fallback to localStorage default
    let apiModel = selectedModelFromToolbar && selectedModelFromToolbar !== '_'
      ? selectedModelFromToolbar
      : localStorageDefaultModel;

    if (selectedModelFromToolbar === '__') apiModel = editorState.models[0]; // use first model from list as default 

    if (!apiModel) {
      console.error("Chat: No API model configured or selected.");
      editorState.addMessage({
        role: 'assistant',
        content: 'Error: API model not configured. Please select a model or set a default in API settings.'
      });
      setIsLoading(false);
      return;
    }

    console.log('Using API model:', apiModel);

    // Parse model and provider
    let model = apiModel;
    let provider = null;

    if (apiModel.includes('@')) {
      const [modelPart, providerPart] = apiModel.split('@');
      model = modelPart;
      provider = providerPart;
    }

    const requestMessages = await getMessages(input, images);

    // Build request body
    const requestBody = {
      model: model,
      messages: requestMessages,
      stream: true
    };

    // Add provider if specified
    if (provider) {
      requestBody.provider = { only: [provider] };
    }


    try {
      // Start timing the API call
      const startTime = Date.now();

      const response = await fetch(`${apiUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('API request failed');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let currentMessage = '';
      let completeResponse; // Variable to hold parsed JSON
      let lineBuffer = ''; // Buffer for assembling complete lines

      while (true) {
        const { done, value } = await reader.read();
        // Pass { stream: true } to decode if you expect multi-byte characters
        // to be split across chunks. For simple text, it might not be strictly necessary
        // but is safer.
        lineBuffer += decoder.decode(value, { stream: !done });

        if (done) {
          // Process any remaining data in buffer if the stream ends without a final newline
          // This is a fallback, as SSE usually ends lines with \n
          if (lineBuffer.startsWith('data: ')) {
            const data = lineBuffer.slice(6).trim();
            if (data !== '[DONE]' && data !== '') {
              try {
                const parsed = JSON.parse(data);
                const content = parsed.choices[0]?.delta?.content || '';
                currentMessage += content;
                // Note: completeResponse is not updated here with 'parsed'
              } catch (e) {
                console.error('Failed to parse final JSON from stream:', data, e);
              }
            }
          }
          break; // Exit the loop
        }

        let eolIndex;
        // Process all complete lines in the buffer
        while ((eolIndex = lineBuffer.indexOf('\n')) >= 0) {
          const line = lineBuffer.slice(0, eolIndex).trim(); // Get the line and trim
          lineBuffer = lineBuffer.slice(eolIndex + 1); // Remove processed line from buffer

          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim(); // Get data part and trim
            if (data === '[DONE]') continue; // OpenAI specific: stream is finished
            if (data === '') continue; // Skip empty data lines

            try {
              completeResponse = JSON.parse(data); // completeResponse is updated with the current chunk
              const content = completeResponse.choices[0]?.delta?.content || '';
              currentMessage += content;
              //if (currentMessage.indexOf("inking") > -1) debugger;

              if (!thinkingMessageShown) {
                let { extractedText, remainingMessage } = extractTag(currentMessage, 'atThinking');
                let thinkingMessage = extractedText || '';
                let rmsg = remainingMessage;


                ({ extractedText, remainingMessage } = extractTag(rmsg, 'Thinking')); // google flash returns sometimes Thinking tag
                if (extractedText) thinkingMessage = thinkingMessage ? thinkingMessage + '\n' + extractedText : extractedText;
                rmsg = remainingMessage;

                if (thinkingMessage) {
                  editorState.addMessage({ role: 'assistant', content: thinkingMessage });
                  thinkingMessageShown = true;
                  currentMessage = rmsg;
                  console.log('Thinking message shown:', thinkingMessage, ' Remaining message:', rmsg);
                }
              }
            } catch (e) {
              // ignore console.error('Failed to parse JSON from stream:', data, e);              
            }
          } else if (line !== '') {
            // Non-data lines, like comments (starting with ':') or empty lines for keep-alive
            // console.log('SSE non-data line:', line);
          }
        }
      } // End of while loop for streaming

      // After streaming is complete, process the full message
      if (currentMessage) {
        // Stop timing the API call
        const endTime = Date.now();
        const elapsedSeconds = ((endTime - startTime) / 1000).toFixed(1);
        
        let costInfo = null;
        let cost = "";
        // Call getCost with the ID from the last processed data chunk (held in completeResponse)
        if (completeResponse && completeResponse.id) {
          costInfo = await getCost(completeResponse.id);
          if (costInfo) cost = "$" + costInfo.data.total_cost.toFixed(3);
        } else {
          console.log("No ID found in the last processed stream data chunk (via completeResponse.id) to fetch cost, or completeResponse is undefined.");
        }

        // Call the new processLLMResponseAndUpdateState function
        try {
          const finalmsg = processLLMResponseAndUpdateState(currentMessage, () => { });
          console.log('LLM response processed:', finalmsg);
          if (finalmsg.specificationUpdateReasoning) {
            editorState.addMessage({
              role: 'assistant',
              content: finalmsg.specification ? "Specification updated" : "No specification update",
              detail: finalmsg.specificationUpdateReasoning + (finalmsg.specification ? `\n\nNew Specification:\n${finalmsg.specification}` : '')
            });
          }

          const assistantMessageContent = (finalmsg.result ? finalmsg.result : "Done") + " " + cost + " " + elapsedSeconds + "s";
          if (typeof finalmsg.version !== 'undefined') {
            editorState.addMessage({
              role: 'assistant',
              content: assistantMessageContent,
              linkDetails: {
                text: `Version ${1 + finalmsg.version}`,
                action: 'selectHistoryVersion',
                arg: finalmsg.version
              }
            });
          } else {
            editorState.addMessage({
              role: 'assistant',
              content: assistantMessageContent
            });
          }

        } catch (error) {
          console.error('Error processing LLM response:', error);
        }

        // Existing logic for color boxes:
        console.log('Final message:', currentMessage);

        const detectedColors = detectColors(currentMessage);
        if (detectedColors.length > 0) {
          // Update to use editorState for color detection
          const currentMessages = [...editorState.chat.messages];
          const assistantIndex = currentMessages.findIndex((msg, idx) =>
            idx === currentMessages.length - 1 && msg.role === 'assistant' && !msg.isColorBox
          );

          if (assistantIndex !== -1) {
            const colorMessages = [];
            if (detectedColors.includes('red')) {
              colorMessages.push({
                role: 'assistant',
                content: '',
                isColorBox: true,
                color: 'red'
              });
            }
            if (detectedColors.includes('yellow')) {
              colorMessages.push({
                role: 'assistant',
                content: '',
                isColorBox: true,
                color: 'yellow'
              });
            }
            if (detectedColors.includes('green')) {
              colorMessages.push({
                role: 'assistant',
                content: '',
                isColorBox: true,
                color: 'green'
              });
            }
            if (detectedColors.includes('green')) {
              colorMessages.push({
                role: 'assistant',
                content: '',
                isColorBox: true,
                color: 'green'
              });
            }
            //currentMessages.splice(assistantIndex, 0, ...colorMessages);
            editorState.updateMessages(currentMessages);
          }
        }
      }
    } catch (error) {
      console.error('Error:', error);
      // Update to use editorState
      editorState.addMessage({
        role: 'assistant',
        content: 'Error: Failed to connect to API. Please check your API configuration in localStorage.'
      });
      setInput(userMessage); // Restore the input field
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ChatUI
      messages={messages}
      input={input}
      setInput={setInput}
      isLoading={isLoading}
      expandedMessages={expandedMessages}
      setExpandedMessages={setExpandedMessages}
      onSubmit={handleSubmit}
      onToggleExpand={toggleExpand}
      specification={specification}
    />
  );
};

export default Chat;